// The wheel's node editors: panel, trait, SNP and — since A5 — an ID-CARRYING gene, which R6 can now
// also CREATE with no SNPs under it. An INLINE section of the build column, never a modal: selecting a
// node IS the edit intent, so there is no button to open and nothing to dismiss.
// Rendered ONLY by WheelDashboard in build mode, so WheelModal stays incapable of showing an editor.
// A gene with no wheel_genes row still has no EDITOR — there is nothing server-side to address.
// Gene delete lives on the inspector, not in this footer.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, Popconfirm } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { useNotifications } from "../../../../context/NotificationContext";
import { createPanel, deletePanel, updatePanel } from "../../../../api/geneCodes/PanelApi";
import { createTrait, deleteTrait, updateTrait } from "../../../../api/geneCodes/TraitApi";
import { createStagingRecord, deleteStagingRecord, updateStagingRecord } from "../../../../api/geneCodes/StagingApi";
import { createTraitGene, reorderTraitGenes, updateWheelGene } from "../../../../api/geneCodes/WheelGeneApi";
import { Panel, defaultPanel } from "../../../../types/geneCodes/Panel";
import { Trait, defaultTrait } from "../../../../types/geneCodes/TraitTypes";
import { Staging, defaultStaging } from "../../../../types/geneCodes/StagingTypes";
import { RegistryGene } from "../../../../types/geneCodes/RegistryTypes";
import { WheelNodeKind, kindLabel, panelStatusOf, parseBuilderTask } from "../../../../utils/geneCodes/wheelAuthoring";
import { geneRingForTrait } from "../../../../utils/geneCodes/wheelGeneRing";
import { GeneFields, LockedField, PanelFields, SnpFields, TraitFields } from "./nodeEditorForms";
import "./wheelBuilder.css";

export interface NodeMutation {
	kind: WheelNodeKind;
	action: "create" | "update" | "delete";
	panelId?: number;
}

interface WheelNodeEditorProps {
	task: string;
	panel: Panel | null;
	trait: Trait | null;
	gene: string | null;
	snp: Staging | null;
	snps: Staging[];
	onClose: () => void;
	onSaved: (mutation: NodeMutation) => void;
}

const ROW_LABEL: Record<"panel" | "trait" | "gene" | "snp", string> = { panel: "panels", trait: "traits", gene: "wheel_genes", snp: "staging" };

const errorsFrom = (error: unknown): string[] => {
	if (axios.isAxiosError(error) && error.response) {
		const data = error.response.data;
		if (data?.errors) return Object.values(data.errors).flat() as string[];
		if (data?.message) return [data.message];
	}
	if (error instanceof Error && error.message) return [error.message];
	return ["An unknown error occurred."];
};

const WheelNodeEditor: React.FC<WheelNodeEditorProps> = ({ task, panel, trait, gene, snp, snps, onClose, onSaved }) => {
	const [form] = Form.useForm();
	const { showSuccess, showError } = useNotifications();
	const [submitting, setSubmitting] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);

	const parsed = parseBuilderTask(task);
	const kind = parsed?.kind;
	const editing = parsed?.action === "edit";

	// A5: the trait's ring, ONE derivation (wheelGeneRing.ts) — the same order the wheel draws.
	const ring = useMemo(() => (trait ? geneRingForTrait(trait, snps) : []), [trait, snps]);
	const geneIndex = kind === "gene" && gene ? ring.findIndex((entry) => entry.name === gene) : -1;
	const geneEntry = geneIndex >= 0 ? ring[geneIndex] : null;

	// The gene editor's two non-form values: the pending ring position and the registry link.
	const [genePosition, setGenePosition] = useState(0);
	const [geneRegistryRef, setGeneRegistryRef] = useState<string | null>(null);

	// An id-LESS gene still has no EDITOR — nothing server-side to address until the backfill runs.
	// Creating one is a different question: R6's route mints the row, so there is nothing to address yet.
	const editable = kind === "panel" || kind === "trait" || kind === "snp" || (kind === "gene" && (!editing || geneEntry?.wheelGeneId != null));
	const record = kind === "panel" ? panel : kind === "trait" ? trait : kind === "gene" ? geneEntry : snp;
	// A new node inherits its position from the wheel, so it needs its parents resolved first.
	const parentReady = kind === "panel" ? true : kind === "trait" ? Boolean(panel) : Boolean(panel && trait);
	const ready = Boolean(parsed) && editable && (editing ? Boolean(record) : parentReady);

	const initialValues = useMemo(() => {
		if (kind === "panel") return editing && panel ? { ...panel } : { ...defaultPanel };
		if (kind === "trait") return editing && trait ? { ...trait } : { ...defaultTrait, trait_panel: panel?.name_panel ?? "" };
		if (kind === "gene") return { gene_symbol: editing ? gene ?? "" : "" };
		if (kind === "snp") {
			if (editing && snp) return { ...snp };
			return { ...defaultStaging, category: panel?.name_panel ?? null, subcategory: trait?.trait_name ?? null, gene: gene ?? null };
		}
		return {};
	}, [kind, editing, panel, trait, gene, snp]);

	useEffect(() => {
		if (!ready) return;
		setErrorMessages([]);
		form.resetFields();
		form.setFieldsValue(initialValues);
	}, [ready, form, initialValues]);

	// Once per node, deliberately — the ring refetches mid-edit and must not yank a pending move back.
	useEffect(() => {
		if (!ready || kind !== "gene") return;
		setGenePosition(editing ? geneIndex : ring.length);
		setGeneRegistryRef(editing ? geneEntry?.registryRef ?? null : null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ready, kind]);

	if (!ready || !kind || !editable) return null;

	const label = kindLabel(kind);
	const traitSnpCount = trait ? snps.filter((row) => row.subcategory === trait.trait_name).length : 0;

	// A new wheel has no parent to name; everything else is placed by the wheel it belongs to.
	const subtitle = kind === "panel" && !editing ? "New wheel" : panel ? `${panel.name_panel} · ${panelStatusOf(panel)}` : "";

	const runMutation = async (mutation: () => Promise<NodeMutation>, verbs: { done: string; failed: string }, busy: (value: boolean) => void) => {
		busy(true);
		setErrorMessages([]);
		try {
			const outcome = await mutation();
			showSuccess(`${label.charAt(0).toUpperCase()}${label.slice(1)} ${verbs.done}.`);
			onSaved(outcome);
		} catch (error) {
			setErrorMessages(errorsFrom(error));
			showError(`Failed to ${verbs.failed} ${label}.`);
		} finally {
			busy(false);
		}
	};

	const handleSubmit = async () => {
		let values: Record<string, unknown>;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}

		await runMutation(
			async () => {
				if (kind === "panel") {
					if (editing && panel) {
						await updatePanel(panel.id_panels, { ...panel, ...values });
						return { kind, action: "update", panelId: panel.id_panels };
					}
					const created = await createPanel({ ...defaultPanel, ...values });
					return { kind, action: "create", panelId: created?.id_panels };
				}

				if (kind === "trait") {
					if (editing && trait) {
						await updateTrait(trait.idtraits, { ...trait, ...values });
						return { kind, action: "update" };
					}
					await createTrait({ ...defaultTrait, trait_panel: panel?.name_panel ?? "", ...values });
					return { kind, action: "create" };
				}

				if (kind === "gene" && !editing && trait) {
					// sort_order stays null: an appended gene sorts after the ordered rows, server and client alike.
					await createTraitGene(trait.idtraits, { gene_symbol: String(values.gene_symbol ?? "").trim(), registry_ref: geneRegistryRef });
					return { kind, action: "create" };
				}

				if (kind === "gene" && geneEntry?.wheelGeneId != null && trait) {
					const symbol = String(values.gene_symbol ?? "").trim();
					// PATCH carries only what changed — an unchanged symbol must not re-run the cascade.
					const patch: { gene_symbol?: string; registry_ref?: string | null } = {};
					if (symbol !== geneEntry.name) patch.gene_symbol = symbol;
					if ((geneRegistryRef ?? null) !== (geneEntry.registryRef ?? null)) patch.registry_ref = geneRegistryRef;
					if (Object.keys(patch).length > 0) await updateWheelGene(geneEntry.wheelGeneId, patch);

					if (genePosition !== geneIndex) {
						const moved = [...ring];
						moved.splice(genePosition, 0, ...moved.splice(geneIndex, 1));
						// Subset on purpose: only id-bearing genes exist server-side; the rest keep first-appearance order.
						const orderedIds = moved.map((entry) => entry.wheelGeneId).filter((id): id is number => id != null);
						await reorderTraitGenes(trait.idtraits, orderedIds);
					}

					return { kind, action: "update" };
				}

				if (editing && snp) {
					await updateStagingRecord(snp.idstaging, { ...snp, ...values });
					return { kind, action: "update" };
				}
				// POST /api/staging is the add-node verb: it checks `category` against panels.name_panel and
				// creates the traits row when the subcategory is new. Never addSnpsToTrait, which MOVES a SNP.
				await createStagingRecord({ ...(initialValues as Partial<Staging>), ...values });
				return { kind, action: "create" };
			},
			{ done: editing ? "saved" : "created", failed: "save" },
			setSubmitting,
		);
	};

	const handleDelete = () =>
		runMutation(
			async () => {
				if (kind === "panel" && panel) await deletePanel(panel.id_panels);
				if (kind === "trait" && trait) await deleteTrait(trait.idtraits);
				if (kind === "snp" && snp) await deleteStagingRecord(snp.idstaging);
				return { kind, action: "delete" };
			},
			{ done: "deleted", failed: "delete" },
			setDeleting,
		);

	const cascadeWarning = () => {
		if (!editing) return null;
		if (kind === "panel") {
			return "Renaming a wheel re-points every trait and SNP under it — the wheel's name is the only link, and stored AI reports are keyed by it too. The rename is applied by the server; nothing here rewrites the children.";
		}
		if (kind === "trait") {
			return `Renaming a trait re-points every SNP under it — staging.subcategory is the only link between them. The ${traitSnpCount} ${traitSnpCount === 1 ? "SNP" : "SNPs"} in this trait move with the new name.`;
		}
		if (kind === "gene") {
			const count = geneEntry?.snps.length ?? 0;
			const cascade = `Renaming this gene rewrites the gene name on its ${count} ${count === 1 ? "SNP" : "SNPs"} in this trait — staging.gene is the only link, so every one of them moves with the new symbol.`;
			// A5: published wheels edit IN PLACE — no fork on a manual edit, so the change is live.
			if (panelStatusOf(panel) === "published") return `${cascade} This wheel is published: the edit applies in place — nothing forks — and is live to patients as soon as it saves.`;
			if (panelStatusOf(panel) === "hidden") return `${cascade} Saved in place — not shown to anyone until the wheel is unhidden.`;
			return cascade;
		}
		return null;
	};

	const warning = cascadeWarning();

	return (
		<section className="mg-node-editor" data-testid="node-editor">
			<div className="mg-node-editor__title">
				<span className="mg-node-editor__title-main">
					{editing ? "Edit" : "Add"} {label}
				</span>
				{subtitle ? <span className="mg-node-editor__title-sub">{subtitle}</span> : null}
			</div>

			<div className="mg-node-editor__body" data-testid="node-editor-body" data-kind={kind} data-mode={editing ? "edit" : "create"}>
				{errorMessages.length > 0 && (
					<Alert
						type="error"
						showIcon
						closable
						onClose={() => setErrorMessages([])}
						message={errorMessages.length === 1 ? errorMessages[0] : "Could not save"}
						description={errorMessages.length === 1 ? undefined : errorMessages.join(" ")}
					/>
				)}

				{kind === "trait" && <LockedField label="Wheel" value={panel?.name_panel ?? "—"} hint="Set by the wheel you are editing." testId="node-editor-locked-wheel" />}

				{kind === "gene" && (
					<div className="mg-node-editor__grid">
						<LockedField label="Wheel" value={panel?.name_panel ?? "—"} hint="Set by the wheel you are editing." testId="node-editor-locked-wheel" />
						<LockedField label="Trait" value={trait?.trait_name ?? "—"} hint="A gene node lives on one trait's ring." testId="node-editor-locked-trait" />
					</div>
				)}

				{kind === "snp" && (
					<div className="mg-node-editor__grid">
						<LockedField label="Wheel" value={(editing ? snp?.category : panel?.name_panel) ?? "—"} hint="Set by the wheel you are editing." testId="node-editor-locked-wheel" />
						<LockedField label="Trait" value={(editing ? snp?.subcategory : trait?.trait_name) ?? "—"} hint="Moving a SNP between traits is a membership change, not an edit." testId="node-editor-locked-trait" />
					</div>
				)}

				<Form form={form} layout="vertical" initialValues={initialValues} className="mg-node-editor__form">
					{kind === "panel" && <PanelFields />}
					{kind === "trait" && <TraitFields />}
					{kind === "gene" && (
						<GeneFields
							creating={!editing}
							registryRef={geneRegistryRef}
							onPickGene={(picked: RegistryGene) => setGeneRegistryRef(picked.id)}
							position={genePosition}
							ringSize={ring.length}
							onMove={(delta) => setGenePosition((previous) => Math.min(Math.max(previous + delta, 0), ring.length - 1))}
						/>
					)}
					{kind === "snp" && <SnpFields creating={!editing} />}
				</Form>

				{warning ? (
					<div className="mg-node-editor__cascade" data-testid="node-editor-cascade">
						<WarningOutlined />
						<span>{warning}</span>
					</div>
				) : null}
			</div>

			<div className="mg-node-editor__footer">
				<span className="mg-node-editor__footer-hint">
					Writes one <code>{ROW_LABEL[kind]}</code> row
				</span>
				<div className="mg-node-editor__footer-actions">
					{/* Gene delete is the inspector's Popconfirm — its blast radius names the SNPs it detaches. */}
					{editing && kind !== "gene" ? (
						<Popconfirm
							title={`Delete this ${label}?`}
							description={kind === "snp" ? "The gene ring segment disappears with its last SNP." : `Everything under this ${label} loses its link.`}
							okText="Delete"
							okButtonProps={{ danger: true, "data-testid": "node-editor-delete-confirm" } as never}
							cancelText="Cancel"
							onConfirm={handleDelete}
						>
							<Button danger loading={deleting} data-testid="node-editor-delete">
								Delete
							</Button>
						</Popconfirm>
					) : null}
					{/* Only an add can be abandoned — an edit IS the selection, so there is nothing to cancel back to. */}
					{!editing ? (
						<Button onClick={onClose} data-testid="node-editor-cancel">
							Cancel
						</Button>
					) : null}
					<Button type="primary" loading={submitting} onClick={handleSubmit} data-testid="node-editor-save">
						Save {label}
					</Button>
				</div>
			</div>
		</section>
	);
};

export default WheelNodeEditor;
